const destinationIdsOf = (stop) => [
  ...(stop?.destination_hub_ids || []),
  ...((stop?.destinations || []).map((dest) => dest.id).filter((id) => id != null)),
];

const locationNameKey = (value) => String(value || '').trim().toLowerCase();

/** Receiver belongs on this chip only: the city itself, or a branch under that city. */
const receiverBelongsToChip = (receiverId, chipId, parentMap) => {
  const sid = Number(receiverId);
  const point = Number(chipId);
  if (Number.isNaN(sid) || Number.isNaN(point)) return false;
  if (sid === point) return true;
  if (!parentMap) return false;
  const parent = parentMap[sid];
  return parent != null && Number(parent) === point && Number(parent) !== sid;
};

const receiverHubIdOf = (shipment, locations) => {
  const direct = shipment?.receiver_location_hub_id
    ?? shipment?.receiver_location_hub?.id
    ?? shipment?.receiver_hub_id;
  const numeric = Number(direct);
  if (direct != null && direct !== '' && !Number.isNaN(numeric)) return numeric;

  const name = locationNameKey(
    shipment?.receiver_location || shipment?.consignee_location,
  );
  if (!name) return null;
  const loc = (locations || []).find((item) => locationNameKey(
    item.location || item.hub_name || item.name,
  ) === name);
  if (!loc) return null;
  const id = Number(loc.id ?? loc.hub_id);
  return Number.isNaN(id) ? null : id;
};

/**
 * Shipment counts for the location chips already on the dispatch form.
 * Each city is counted on its own chip. Cities that share a parent hub
 * do not share a count. A crossing hub shows the total for its cities.
 */
export const mlLocationChipCounts = (
  shipments,
  { parentMap = null, toIds = [], crossingStops = [], locations = [] } = {},
) => {
  const counts = {};
  const ensure = (id) => {
    if (id == null || id === '') return;
    const key = String(id);
    if (counts[key] == null) counts[key] = 0;
  };
  (toIds || []).forEach(ensure);
  (crossingStops || []).forEach((stop) => {
    ensure(stop.hub_id);
    destinationIdsOf(stop).forEach(ensure);
  });

  (shipments || []).forEach((shipment) => {
    const receiverId = receiverHubIdOf(shipment, locations);
    if (receiverId == null) return;
    const matched = new Set();
    const add = (id) => {
      if (id == null || id === '') return;
      const key = String(id);
      if (!Object.prototype.hasOwnProperty.call(counts, key) || matched.has(key)) return;
      matched.add(key);
      counts[key] += 1;
    };
    (toIds || []).forEach((id) => {
      if (receiverBelongsToChip(receiverId, id, parentMap)) add(id);
    });
    (crossingStops || []).forEach((stop) => {
      const dests = destinationIdsOf(stop);
      let crossesHere = false;
      dests.forEach((id) => {
        if (receiverBelongsToChip(receiverId, id, parentMap)) {
          crossesHere = true;
          add(id);
        }
      });
      if (crossesHere) add(stop.hub_id);
    });
  });

  return counts;
};

const selectionKey = (form) => [
  (form?.to_location_ids || []).map(String).join(','),
  (form?.crossing_destination_hub_ids || []).map(String).join(','),
  (form?.crossing_location_hub_ids || []).map(String).join(','),
  (form?.crossing_stops || [])
    .map((stop) => `${stop.hub_id}:${(stop.destination_hub_ids || []).join(',')}`)
    .join('|'),
].join('::');

/**
 * Drop To cities and crossing cities that have no selected shipments.
 * A crossing hub is dropped only when none of its cities have a shipment.
 * When every count is still zero, the selection is left as it is.
 */
export const dropDispatchLocationsWithoutShipments = (form, counts) => {
  if (!form || !counts) return form;
  const positive = Object.values(counts).some((n) => Number(n) > 0);
  if (!positive) return form;

  const keep = (id) => Number(counts[String(id)] || 0) > 0;
  const nextTo = (form.to_location_ids || []).filter(keep);
  const nextDest = (form.crossing_destination_hub_ids || []).filter(keep);
  const nextStops = (form.crossing_stops || [])
    .map((stop) => ({
      ...stop,
      destination_hub_ids: (stop.destination_hub_ids || []).filter(keep),
    }))
    .filter((stop) => (stop.destination_hub_ids || []).length && keep(stop.hub_id));
  const nextHubs = nextStops.length
    ? nextStops.map((stop) => stop.hub_id)
    : (form.crossing_location_hub_ids || []).filter(keep);

  const next = {
    ...form,
    to_location_ids: nextTo,
    crossing_destination_hub_ids: nextDest,
    crossing_location_hub_ids: nextHubs,
    crossing_stops: nextStops,
  };
  if (selectionKey(next) === selectionKey(form)) return form;
  return next;
};

/** Keep the current form object when a route sync did not change the locations. */
export const replaceDispatchLocationsIfChanged = (prev, next) => {
  if (!next || next === prev) return prev;
  if (selectionKey(next) === selectionKey(prev)) return prev;
  return next;
};
